import { Box, useTheme } from '@mui/material'
import { FlexGrowRow, InvertableThemeProvider, RedirectWithQuery } from '@xylabs/sdk-react'
import { useContext } from 'react'
import { Helmet } from 'react-helmet'
import { BrowserRouter, Route, Routes } from 'react-router-dom'

import { RedirectToMedium } from './Components'
import { AppSettingsContext } from './Contexts'
import {
  AccountSettingsPage,
  AuctionCardPage,
  AuctionEventsPage,
  AuctionJsonPage,
  AuctionPage,
  AuctionsRoutes,
  BidderEventsPage,
  ContractsPage,
  EntitiesPage,
  EntityPage,
  EthersDebugPage,
  Fork2Page,
  Fork3Page,
  GeotokenJsonPage,
  GeotokenPage,
  GeotokenSettingsPage,
  GeotokensSettingsPage,
  JsonPage,
  PlacePage,
  PlayPage,
  QuadkeyFamilyPage,
  QuadkeyPage,
  RewardsPage,
  RulesPage,
  SettingsPage,
  SplitPage,
  StatsJsonPage,
  StatsPage,
  ThingPage,
  Web3DebugPage,
} from './Pages'
import { themeOptions } from './theme'

const AuctionRoutes: React.FC = () => {
  return (
    <Routes>
      <Route element={<AuctionEventsPage />} path="/:id/events" />
      <Route element={<AuctionJsonPage />} path="/:id/json" />
      <Route element={<AuctionPage />} path="/:id" />
      <Route element={<AuctionCardPage />} path="/:id/card/:size" />
      <Route element={<RedirectToMedium />} path="/:id/card" />
      <Route element={<RedirectWithQuery to="/auctions" />} path="*" />
    </Routes>
  )
}

const GeotokenRoutes: React.FC = () => {
  return (
    <Routes>
      <Route element={<AuctionEventsPage />} path="/:id/events" />
      <Route element={<GeotokenSettingsPage />} path="/:id/settings" />
      <Route element={<GeotokenJsonPage />} path="/:id/json" />
      <Route element={<GeotokenPage />} path="/:id" />
      <Route element={<RedirectWithQuery to="/auctions" />} path="*" />
    </Routes>
  )
}

const QuadkeyRoutes: React.FC = () => {
  return (
    <Routes>
      <Route element={<AuctionEventsPage />} path="/:id/events" />
      <Route element={<GeotokenJsonPage />} path="/:id/json" />
      <Route element={<QuadkeyPage />} path="/:id" />
      <Route element={<QuadkeyFamilyPage />} path="/:id/family" />
      <Route element={<QuadkeyPage />} path="*" />
    </Routes>
  )
}

const ThingRoutes: React.FC = () => {
  return (
    <Routes>
      <Route element={<ThingPage />} path="/:id" />
    </Routes>
  )
}

const PlaceRoutes: React.FC = () => {
  return (
    <Routes>
      <Route element={<PlacePage />} path="/:id" />
    </Routes>
  )
}

const DebugRoutes: React.FC = () => {
  return (
    <Routes>
      <Route element={<Web3DebugPage />} path="/web3" />
      <Route element={<EthersDebugPage />} path="/ethers" />
      <Route element={<RedirectWithQuery to="/" />} path="*" />
    </Routes>
  )
}

const EntityRoutes: React.FC = () => {
  return (
    <Routes>
      <Route element={<BidderEventsPage />} path="/:id/events" />
      <Route element={<EntityPage />} path="/:id" />
      <Route element={<EntityPage />} path="*" />
    </Routes>
  )
}

const EntitiesRoutes: React.FC = () => {
  return (
    <Routes>
      <Route element={<EntitiesPage />} path="*" />
    </Routes>
  )
}

const StatsRoutes: React.FC = () => {
  return (
    <Routes>
      <Route element={<StatsPage />} path="/" />
      <Route element={<StatsJsonPage />} path="/json" />
      <Route element={<RedirectWithQuery to="/" />} path="*" />
    </Routes>
  )
}

const ListRoutes: React.FC = () => {
  return (
    <Routes>
      <Route element={<RedirectWithQuery to="/auctions" />} path="/" />
      <Route element={<RedirectWithQuery to="/auctions/json" />} path="/json" />
      <Route element={<RedirectWithQuery to="/auctions/live" />} path="/:id" />
      <Route element={<RedirectWithQuery to="/auctions" />} path="*" />
    </Routes>
  )
}

const GeotokensSettingsRoutes: React.FC = () => {
  return (
    <Routes>
      <Route element={<GeotokensSettingsPage />} path="/" />
      <Route element={<GeotokensSettingsPage />} path="/:filter" />
      <Route element={<RedirectWithQuery to="/geotokens/settings" />} path="*" />
    </Routes>
  )
}

const ForkRoutes: React.FC = () => {
  return (
    <Routes>
      <Route element={<Fork2Page />} path="/2" />
      <Route element={<Fork3Page />} path="/3" />
      <Route element={<RedirectWithQuery to="/fork/3" />} path="*" />
    </Routes>
  )
}

const PlayRoutes: React.FC = () => {
  return (
    <Routes>
      <Route element={<PlayPage />} path="/" />
      <Route element={<RedirectWithQuery to="/play" />} path="*" />
    </Routes>
  )
}

const RewardsRoutes: React.FC = () => {
  return (
    <Routes>
      <Route element={<RewardsPage />} path="/" />
      <Route element={<RedirectWithQuery to="/rewards" />} path="*" />
    </Routes>
  )
}

const RouteGroups: React.FC = () => {
  return (
    <Routes>
      <Route element={<PlayRoutes />} path="/play/*" />
      <Route element={<GeotokenRoutes />} path="/geotoken/*" />
      <Route element={<QuadkeyRoutes />} path="/quadkey/*" />
      <Route element={<ThingRoutes />} path="/thing/*" />
      <Route element={<PlaceRoutes />} path="/place/*" />
      <Route element={<AuctionRoutes />} path="/auction/*" />
      <Route element={<AuctionsRoutes />} path="/auctions/*" />
      <Route element={<EntitiesRoutes />} path="/entities/*" />
      <Route element={<RewardsRoutes />} path="/rewards/*" />
      <Route element={<EntityRoutes />} path="/entity/*" />
      <Route element={<ListRoutes />} path="/list/*" />
      <Route element={<StatsRoutes />} path="/stats/*" />
      <Route element={<ForkRoutes />} path="/fork/*" />
      <Route element={<DebugRoutes />} path="/debug/*" />
      <Route element={<GeotokensSettingsRoutes />} path="/geotokens/settings/*" />
      <Route element={<RedirectWithQuery to="/play" />} path="*" />
    </Routes>
  )
}

const PortalThemed: React.FC = () => {
  const theme = useTheme()
  return (
    <Box bgcolor={theme.palette.background.default}>
      <Helmet defaultTitle="XYO World" titleTemplate="%s | XYO World">
        <meta content="https://world.xyo.network/auctions" property="og:url" />
        <meta content="website" property="og:type" />
      </Helmet>
      <FlexGrowRow>
        <BrowserRouter>
          <Routes>
            <Route element={<SettingsPage />} path="/settings" />
            <Route element={<AccountSettingsPage />} path="/account" />
            <Route element={<JsonPage />} path="/json" />
            <Route element={<ContractsPage />} path="/contracts" />
            <Route element={<SplitPage />} path="/split" />
            <Route element={<RulesPage />} path="/rules" />
            <Route element={<RouteGroups />} path="*" />
          </Routes>
        </BrowserRouter>
      </FlexGrowRow>
    </Box>
  )
}

const Portal: React.FC = () => {
  const { darkMode } = useContext(AppSettingsContext)
  return (
    <InvertableThemeProvider dark={darkMode} options={themeOptions}>
      <PortalThemed />
    </InvertableThemeProvider>
  )
}

export default Portal
